import { motion } from "framer-motion";
import { MessageCircle, Instagram, Search, Package } from "lucide-react";
import { SectionHeading, Reveal } from "./Reveal";
import { WA_DEFAULT, INSTAGRAM_URL } from "@/lib/site";

const STEPS = [
    {
        num: "1",
        title: "Browse the collection",
        copy: "Explore our rangoli mats, pooja essentials, potli bags and more — right here or on Instagram.",
        Icon: Search,
    },
    {
        num: "2",
        title: "Message us to order",
        copy: "Say hello on WhatsApp or Instagram DM to confirm the design, availability and price.",
        Icon: MessageCircle,
    },
    {
        num: "3",
        title: "Pick up or get it delivered",
        copy: "Collect your pieces in Point Cook, VIC — or have them delivered anywhere in Australia.",
        Icon: Package,
    },
];

const HowToOrder = () => (
    <section id="order" data-testid="how-to-order-section" className="relative py-28 sm:py-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <SectionHeading
                eyebrow="No Cart, No Checkout — Just a Chat"
                title={<>How to <em className="italic text-[#E2BA03] [text-shadow:0_1px_0_#472500]">Order</em></>}
                testId="how-to-order-heading"
                className="mb-16 sm:mb-24"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
                <div
                    className="hidden md:block absolute top-16 left-[16%] right-[16%] border-t-2 border-dashed border-[#472500]/25"
                    aria-hidden="true"
                />
                {STEPS.map((s, i) => (
                    <motion.div
                        key={s.num}
                        initial={{ y: 44, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.85, delay: i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                        viewport={{ once: true, margin: "-70px" }}
                        className="relative flex flex-col items-center text-center px-4"
                        data-testid={`order-step-${s.num}`}
                    >
                        <div className="relative grid place-items-center w-32 h-32 rounded-full bg-[#F7F4EC] border border-[#472500]/20 mb-8 group">
                            <s.Icon size={34} strokeWidth={1.6} className="text-[#472500]" />
                            <span className="absolute -top-2 -right-2 grid place-items-center w-10 h-10 rounded-full bg-[#E2BA03] font-display italic text-xl text-[#472500] border border-[#472500]/20">
                                {s.num}
                            </span>
                        </div>
                        <h3 className="font-display text-2xl text-[#472500]">{s.title}</h3>
                        <p className="mt-3 text-sm sm:text-base text-[#472500]/65 leading-relaxed max-w-xs">{s.copy}</p>
                    </motion.div>
                ))}
            </div>
            <Reveal delay={0.2} className="mt-20 flex flex-col sm:flex-row items-center justify-center gap-5">
                <a
                    href={WA_DEFAULT}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="order-whatsapp-cta"
                    className="inline-flex items-center gap-3 rounded-full bg-[#E2BA03] text-[#472500] font-semibold px-10 py-5 text-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-15px_rgba(226,186,3,0.8)]"
                >
                    <MessageCircle size={22} strokeWidth={2.2} />
                    Start a WhatsApp Chat
                </a>
                <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="order-instagram-cta"
                    className="inline-flex items-center gap-3 rounded-full border border-[#472500]/40 text-[#472500] font-medium px-10 py-5 text-lg transition-all duration-300 hover:bg-[#472500] hover:text-[#F0EEE8]"
                >
                    <Instagram size={21} />
                    DM @festivetheory
                </a>
            </Reveal>
        </div>
    </section>
);

export default HowToOrder;
